import { Point2 } from '../../../../domain/geometry/geometric-types';
import { projectPointOnSegment } from '../../shapes/util';
import { Diamond, KileTypeSKU, WedgeCalculationResult } from './wedge-types';
import { selectWedgeType } from './wedge-calculations';

// Handle start point drag
export const handleStartPointDrag = (
  newPoint: Point2,
  points: Point2[],
  recalculateWedgePoints: (
    startPoint: Point2,
    endPoint: Point2
  ) => WedgeCalculationResult | null
) => {
  // When dragging start point, always recalculate all wedge points from scratch
  const result = recalculateWedgePoints(newPoint, points[points.length - 1]);
  if (!result) return null;

  return {
    wedgePoints: result.points,
    wedgeKileTypes: result.kileTypes,
    newDiamonds: result.diamonds,
  };
};

// Handle end point drag
export const handleEndPointDrag = (
  newPoint: Point2,
  points: Point2[],
  recalculateWedgePoints: (
    startPoint: Point2,
    endPoint: Point2
  ) => WedgeCalculationResult | null
) => {
  // When dragging end point, always recalculate all wedge points from scratch
  const result = recalculateWedgePoints(points[0], newPoint);
  if (!result) return null;

  return {
    wedgePoints: result.points,
    wedgeKileTypes: result.kileTypes,
    newDiamonds: result.diamonds,
  };
};

// Helper to update diamond configuration and select appropriate wedge types
export const updateSegmentsForMiddlePoint = (
  middleIndex: number,
  constrainedPoint: Point2,
  points: Point2[],
  diamonds: Diamond[],
  kileTypes: KileTypeSKU[],
  unitScale: number
) => {
  const newKileTypes = [...kileTypes];
  const newDiamonds = [...diamonds];

  // We need to update the segments before and after this point
  const segmentsBefore = middleIndex - 1;
  const segmentsAfter = middleIndex;

  if (segmentsBefore >= 0 && unitScale) {
    // Calculate length of segment before in PDF units
    const segmentBeforeLength = Math.sqrt(
      Math.pow(constrainedPoint[0] - points[middleIndex - 1][0], 2) +
        Math.pow(constrainedPoint[1] - points[middleIndex - 1][1], 2)
    );
    // Convert to meters
    const segmentBeforeLengthMeters = segmentBeforeLength * unitScale;

    // For the segment before:
    // - If it's the first segment (segmentsBefore === 0), triangle1 and triangle3 should be false
    // - Otherwise it should have all triangles true
    let segmentBeforeDiamond: Diamond;
    if (segmentsBefore === 0) {
      // First segment diamond (triangle1,3 false)
      segmentBeforeDiamond = {
        triangle1: false,
        triangle2: true,
        triangle3: false,
        triangle4: true,
      };
    } else {
      // Middle segment diamond (all triangles true)
      segmentBeforeDiamond = {
        triangle1: true,
        triangle2: true,
        triangle3: true,
        triangle4: true,
      };
    }

    newDiamonds[segmentsBefore] = segmentBeforeDiamond;
    newKileTypes[segmentsBefore] = selectWedgeType(
      segmentBeforeDiamond,
      segmentBeforeLengthMeters
    );
  }

  if (segmentsAfter < points.length - 1 && unitScale) {
    // Calculate length of segment after in PDF units
    const segmentAfterLength = Math.sqrt(
      Math.pow(points[middleIndex + 1][0] - constrainedPoint[0], 2) +
        Math.pow(points[middleIndex + 1][1] - constrainedPoint[1], 2)
    );
    // Convert to meters
    const segmentAfterLengthMeters = segmentAfterLength * unitScale;

    // For the segment after:
    // - If it's the last segment (segmentsAfter === points.length - 2), triangle2 and triangle4 should be false
    // - Otherwise it should have all triangles true
    let segmentAfterDiamond: Diamond;
    if (segmentsAfter === points.length - 2) {
      // Last segment diamond (triangle2,4 false)
      segmentAfterDiamond = {
        triangle1: true,
        triangle2: false,
        triangle3: true,
        triangle4: false,
      };
    } else {
      // Middle segment diamond (all triangles true)
      segmentAfterDiamond = {
        triangle1: true,
        triangle2: true,
        triangle3: true,
        triangle4: true,
      };
    }

    newDiamonds[segmentsAfter] = segmentAfterDiamond;
    newKileTypes[segmentsAfter] = selectWedgeType(
      segmentAfterDiamond,
      segmentAfterLengthMeters
    );
  }

  return { newKileTypes, newDiamonds };
};

// Handle middle point drag
export const handleMiddlePointDrag = (
  newPoint: Point2,
  middleIndex: number,
  points: Point2[],
  diamonds: Diamond[],
  kileTypes: KileTypeSKU[],
  unitScale: number
) => {
  if (
    middleIndex < points.length &&
    middleIndex > 0 &&
    middleIndex < points.length - 1 &&
    unitScale
  ) {
    const newPoints = [...points];

    // Get the first and last points to define the line
    const firstPoint = points[0];
    const lastPoint = points[points.length - 1];

    // Project and constrain the new point onto the line
    const constrainedPoint = projectPointOnSegment(newPoint, [
      firstPoint,
      lastPoint,
    ]);

    // Update the point
    newPoints[middleIndex] = constrainedPoint;

    // Update affected segments
    const { newKileTypes, newDiamonds } = updateSegmentsForMiddlePoint(
      middleIndex,
      constrainedPoint,
      points,
      diamonds,
      kileTypes,
      unitScale
    );

    return { newPoints, newKileTypes, newDiamonds };
  }

  return null;
};
